import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

type PaginationAndSortParams = {
    readonly page: number;
    readonly setPage: (nextPage: number) => void;
    readonly pageSize: number;
    readonly sortBy: string;
    readonly setSortBy: (newSortOrder: string) => void;
    readonly resetPageParam: (searchParams: URLSearchParams) => URLSearchParams;
};

type PaginationAndSortOpts = {
    readonly defaultPageSize?: number;
    readonly defaultSortBy?: string;
};

export const SORT_BY_QUERY_PARAM = 'sortBy';
export const PAGE_QUERY_PARAM = 'page';
export const PAGE_SIZE_QUERY_PARAM = 'pageSize';

export function usePaginationAndSortParams({
    defaultPageSize = 50,
    defaultSortBy = 'orderDateNewToOld'
}: PaginationAndSortOpts = {}): PaginationAndSortParams {
    const [searchParams, setSearchParams] = useSearchParams();

    const page = Math.max(0, parseInt(searchParams.get(PAGE_QUERY_PARAM) ?? '1') - 1);
    const pageSize = Math.max(
        0,
        parseInt(searchParams.get(PAGE_SIZE_QUERY_PARAM) ?? Math.max(1, defaultPageSize).toString())
    );
    const sortBy = searchParams.get(SORT_BY_QUERY_PARAM) ?? defaultSortBy;

    const setPage = useCallback(
        (nextPage: number) => {
            setSearchParams(
                searchParams => {
                    if (nextPage <= 0) {
                        searchParams.delete(PAGE_QUERY_PARAM);
                    } else {
                        searchParams.set(PAGE_QUERY_PARAM, (nextPage + 1).toString());
                    }
                    return searchParams;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const setSortBy = useCallback(
        (newSortOrder: string) => {
            setSearchParams(
                searchParams => {
                    searchParams.set(SORT_BY_QUERY_PARAM, newSortOrder);
                    return resetPageParam(searchParams);
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    return {
        page,
        setPage,
        pageSize,
        sortBy,
        setSortBy,
        resetPageParam
    };
}

export function resetPageParam(nextSearchParams: URLSearchParams) {
    nextSearchParams.delete(PAGE_QUERY_PARAM);
    return nextSearchParams;
}
